import { useCallback, useEffect, useState } from "react";

import { SpinnerLoader } from "components/loader/loader";
import { PageFooter } from "components/shared/page-footer";
import UnauthLayout from "components/shared/unauth-layout";
import { ORIGINS } from "consts";
import { isError } from "data/ss-error";
import { useKeystone } from "data/ss-store";
import {
  useDecodeDataMutation,
  usePaperlessDocumentsEnrollFromEmailMutation,
} from "gql/__generated__/hooks";
import {
  logException,
  trackEvent,
  urlSearchParamsObject,
  useFlags,
} from "utils";

import {
  AlreadyEnrolledStatus,
  EnrollmentErrorStatus,
  EnrollmentSuccessfulStatus,
} from "./paperless-enrollment-status";
import styles from "./paperless-enrollment.module.scss";

enum enrollmentStatusStates {
  SUCCESS,
  CONFLICT,
  ERROR,
  LOADING,
}

const trackPaperlessEnrollmentEvent = async (eventName, data) => {
  trackEvent(eventName, {
    ...data,
    enrollmentOrigin: ORIGINS.PAPERLESS_ENROLLMENT_EMAIL,
  });
};

export const PaperlessEnrollment = () => {
  const { retireProxy } = useFlags();
  const [paperlessDocumentsEnrollFromEmail] =
    usePaperlessDocumentsEnrollFromEmailMutation();

  const [decodeData] = useDecodeDataMutation();

  const [currentState, setCurrentState] = useState(
    enrollmentStatusStates.LOADING
  );

  const enrollInPaperlessDocuments = useCallback(
    async (encodedData) => {
      try {
        const { data } = await paperlessDocumentsEnrollFromEmail({
          variables: { data: encodedData },
        });

        const { success, errorCode, errors } =
          data?.paperlessDocumentsEnrollFromEmail ?? {};

        if (success) {
          return {
            event: "Paperless Documents Enrolled",
            state: enrollmentStatusStates.SUCCESS,
          };
        } else {
          return {
            event: "Paperless Documents Enrollment Error Displayed",
            state:
              errorCode === 409
                ? enrollmentStatusStates.CONFLICT
                : enrollmentStatusStates.ERROR,
            errorCode,
            errorMessage: errors?.length && errors[0],
          };
        }
      } catch (err) {
        logException(err);
        return {
          event: "Paperless Documents Enrollment Error Displayed",
          state: enrollmentStatusStates.ERROR,
          errorCode: 500,
          errorMessage: "Server error",
        };
      }
    },
    [paperlessDocumentsEnrollFromEmail]
  );

  const keystone = useKeystone();
  const decodeUserData = useCallback(
    async (encodedData) => {
      try {
        let responseData;
        if (retireProxy) {
          const res = await keystone.decodeData(encodedData);
          if (!isError(res)) responseData = res;
        } else {
          const { data } = await decodeData({
            variables: { data: encodedData },
          });
          responseData = data?.decodeData;
        }

        const { policyId: policyNumber, email } =
          responseData?.decodeData ?? {};

        return { policyNumber, email };
      } catch (err) {
        logException(err);
        return null;
      }
    },
    [decodeData, retireProxy, keystone]
  );

  useEffect(() => {
    const { data: encodedData } = urlSearchParamsObject();
    const enroll = async () => {
      const decodedUserData = await decodeUserData(encodedData);
      trackPaperlessEnrollmentEvent(
        "Paperless Documents Enrollment Page Loaded",
        decodedUserData
      );

      const { event, state, ...rest } = await enrollInPaperlessDocuments(
        encodedData
      );
      trackPaperlessEnrollmentEvent(event, {
        ...decodedUserData,
        ...rest,
      });
      setCurrentState(state);
    };
    enroll();
  }, [decodeUserData, enrollInPaperlessDocuments]);

  if (currentState === enrollmentStatusStates.LOADING) {
    return (
      <UnauthLayout size="lg">
        <div className={styles.loadingWrapper}>
          <SpinnerLoader firstMessage="Enrollment in progress" />
        </div>
      </UnauthLayout>
    );
  }

  return (
    <>
      {currentState === enrollmentStatusStates.SUCCESS && (
        <EnrollmentSuccessfulStatus />
      )}
      {currentState === enrollmentStatusStates.CONFLICT && (
        <AlreadyEnrolledStatus />
      )}
      {currentState === enrollmentStatusStates.ERROR && (
        <EnrollmentErrorStatus />
      )}
      <PageFooter />
    </>
  );
};
